import { store } from '@/store'
import { defineStore } from 'pinia'
import { getAccessToken, removeToken } from '@/utils/auth'
import { CACHE_KEY, useCache, deleteUserCache } from '@/hooks/web/useCache'
import { getInfo, loginOut } from '@/api/login'

const { wsCache } = useCache()

interface UserVO {
  id: number
  avatar: string
  nickname: string
  deptId: number
  email: string
}

interface UserInfoVO {
  // USER 缓存
  permissions: Set<string>
  roles: string[]
  isSetUser: boolean
  user: UserVO,
  organization: OrganizationVO
}

export interface OrganizationVO {
  // ID
  id?: number

  // --- 基础信息 ---
  orgLicenseUrl?: string
  orgName?: string
  orgCertType?: number
  orgCertNum?: string
  legalRepName?: string
  legalRepCertType?: number
  legalRepCertNum?: string
  orgMobile?: string
  orgLandline?: string
  orgContactReserve?: string
  orgRole?: number

  // --- 企业对公账户 ---
  corpAccountName?: string
  corpBankName?: string
  corpBankBranchName?: string
  corpBankAccount?: string
  bankUnionCode?: string

  // --- 企业联系人信息 ---
  contactName?: string
  contactPhone?: string
  remark?: string
  intro?: string

  // --- 认证信息 ---
  realnameStatus?: number
  authStatus?: boolean
  authFlowStatus?: number
  authFlowRejectReason?: string
  authFlowSubmitTime?: string
  authFlowAuditTime?: string
  adminUserId?: number
}

export const useUserStore = defineStore('admin-user', {
  state: (): UserInfoVO => ({
    permissions: new Set<string>(),
    roles: [],
    isSetUser: false,
    user: {
      id: 0,
      avatar: '',
      nickname: '',
      deptId: 0,
      email: ''
    },
    organization: {
      id: 0,
    }
  }),
  getters: {
    getPermissions(): Set<string> {
      return this.permissions
    },
    getRoles(): string[] {
      return this.roles
    },
    getIsSetUser(): boolean {
      return this.isSetUser
    },
    getUser(): UserVO {
      return this.user
    },
    getEnte(): OrganizationVO {
      return this.organization
    },
    getIsLogin(): boolean {
      return !!getAccessToken() && this.isSetUser
    }
  },
  actions: {
    async setUserInfoAction() {
      if (!getAccessToken()) {
        this.resetState()
        return null
      }
      let userInfo = wsCache.get(CACHE_KEY.USER)
      if (!userInfo) {
        userInfo = await getInfo()
      } else {
        // 特殊：在有缓存的情况下，进行加载。但是即使加载失败，也不影响后续的操作，保证可以进入系统
        try {
          userInfo = await getInfo()
        } catch (error) { }
      }
      this.permissions = new Set(userInfo.permissions || []) // 兜底为 [] https://t.zsxq.com/xCJew
      this.roles = userInfo.roles
      this.user = userInfo.user
      this.organization = userInfo.organization || {}
      this.isSetUser = true
      wsCache.set(CACHE_KEY.USER, userInfo)
      wsCache.set(CACHE_KEY.ROLE_ROUTERS, userInfo.menus)
    },
    async setUserAvatarAction(avatar: string) {
      const userInfo = wsCache.get(CACHE_KEY.USER)
      // NOTE: 是否需要像`setUserInfoAction`一样判断`userInfo != null`
      this.user.avatar = avatar
      userInfo.user.avatar = avatar
      wsCache.set(CACHE_KEY.USER, userInfo)
    },
    async setUserNicknameAction(nickname: string) {
      const userInfo = wsCache.get(CACHE_KEY.USER)
      // NOTE: 是否需要像`setUserInfoAction`一样判断`userInfo != null`
      this.user.nickname = nickname
      userInfo.user.nickname = nickname
      wsCache.set(CACHE_KEY.USER, userInfo)
    },
    async loginOut() {
      await loginOut()
      removeToken()
      deleteUserCache() // 删除用户缓存
      this.resetState()
    },
    resetState() {
      this.permissions = new Set<string>()
      this.roles = []
      this.isSetUser = false
      this.user = {
        id: 0,
        avatar: '',
        nickname: '',
        deptId: 0,
        email: ''
      },
        this.organization = {
          id: 0,
        }
    }
  }
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
